import { onBeforeUnmount, onMounted, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { measureHeight } from '../utils/measureHeight';

// Read by `.cart-clearance` in checkout.css, which is what the screens behind the sheet
// actually apply. Exported so a spec asserts the same token the code writes.
export const CART_SHEET_HEIGHT_PROPERTY = '--vp-cart-sheet-height';

/**
 * Publishes the mobile cart sheet's resting height on `<html>` as a px custom property,
 * so the screens behind it can reserve exactly that much space instead of a constant.
 *
 * `resting` is false while the sheet is expanded or being dragged. Both states are
 * deliberately ignored rather than tracked: the expanded sheet sits under a backdrop
 * overlay, so the content behind it is not meant to be reachable, and following the drag
 * would repaint the page on every pointer frame. The last resting value stays in place
 * until the sheet settles again.
 *
 * The published value is always a measured px length. That matters inside a frame: the
 * host sizes the iframe to the height checkout reports, so anything sized in viewport
 * units feeds a runaway resize loop (see the `vp-checkout-framed` block in checkout.css).
 * Measuring the sheet opens a new route into that loop — the sheet's `max-height: 92vh`
 * was previously harmless only because a fixed element contributes nothing to the in-flow
 * height we report, and this turns it into in-flow padding. So that clamp is now
 * neutralised while framed too, and the value published here has no viewport term left in
 * it at all.
 *
 * Written to `<html>` rather than passed down: the sheet and the three screens that
 * reserve for it are siblings under different page roots, the value is a pure layout
 * measurement, and `<html>` is outside Vue's hydrated tree, so writing it cannot cause a
 * hydration mismatch on the `ssr: true` tickets page. Same reasoning as
 * useParentFrameSync's marker class.
 *
 * Only meaningful below `xl`, where the sheet is `fixed`. Above it the cart is in flow
 * and the property describes a full-height column, which `.cart-clearance` ignores.
 */
export const useCartReservation = (
  cart: MaybeRefOrGetter<HTMLElement | null>,
  resting: MaybeRefOrGetter<boolean>,
): void => {
  let observer: ResizeObserver | undefined;
  // Captured once, so the watcher below cannot end up measuring a different element from
  // the one the observer is attached to. The source is typed loosely per the composable
  // convention, but the element it resolves to at mount is the one this owns.
  let sheet: HTMLElement | undefined;

  const publish = (height: number) => {
    document.documentElement.style.setProperty(CART_SHEET_HEIGHT_PROPERTY, `${height}px`);
  };

  onMounted(() => {
    sheet = toValue(cart) ?? undefined;
    if (!sheet) {
      return;
    }

    // The initial observe callback fires with the mounted size, so the first resting
    // height lands without any extra plumbing.
    observer = new ResizeObserver(entries => {
      if (!toValue(resting) || !sheet) {
        return;
      }

      publish(measureHeight(sheet, entries[0]));
    });
    observer.observe(sheet);
  });

  // Settling back to rest usually resizes the sheet and so fires the observer, but not
  // when a drag ends at the height it started from. `post` flush is required, not
  // incidental: the default `pre` would read the element before the DOM reflects the
  // change that fired this watcher, and publish the height of the state being left.
  watch(
    () => toValue(resting),
    isResting => {
      if (!isResting || !sheet) {
        return;
      }

      publish(measureHeight(sheet));
    },
    { flush: 'post' },
  );

  onBeforeUnmount(() => {
    observer?.disconnect();
    sheet = undefined;
    // A client-side navigation to a screen with no cart would otherwise keep reserving
    // the last sheet's height for a sheet that is no longer on the page. Unconditional
    // is right while pages swap without a transition — Vue unmounts the outgoing cart
    // before mounting the incoming one. Wrapping the page in <Transition>/<Suspense>
    // would invert that, and the departing cart would wipe the arriving cart's value.
    document.documentElement.style.removeProperty(CART_SHEET_HEIGHT_PROPERTY);
  });
};
